import React, { useCallback } from 'react';
import find from 'lodash/find';
import toString from 'lodash/toString';

import { IconsEnum } from '../../../../../assets/icons/types';
import {
  UniversalFilterOptionSelectOption,
  UniversalFilterOptionSelectOnChange
} from './UniversalFilterOptionSelect.types';

import { useDropdownState } from '../../../../../helpers/dropdowns/hooks/useDropdownState';

import { UniversalFilterSelectItem } from '../UniversalFilterSelectItem';

import { RefButtonHelper } from '../../../../../helpers/buttons/RefButtonHelper';
import { Popover } from '../../../../../helpers/Popover';
import { Translate } from '../../../../../helpers/Translate';
import { Icon } from '../../../../../helpers/Icon';

import { PopoverPlacement } from '../../../../../helpers/Popover/popoverConstants';
import { PositionStyleEnum } from '../../../../../types';

const dropdownMenuOffset = 12;

interface UniversalFilterOptionSelectProps<T> {
  selectedValue: T;
  options: UniversalFilterOptionSelectOption<T>[];
  onChange: UniversalFilterOptionSelectOnChange<T>;
}

function UniversalFilterOptionSelect<T>({
  selectedValue,
  options,
  onChange
}: UniversalFilterOptionSelectProps<T>) {
  const selectedOption = find(options, ['value', selectedValue]);

  const {
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover
  } = useDropdownState();

  const handleChange = useCallback<(value: T) => void>(
    (value) => {
      onChange(value);
      closePopover();
    },
    [onChange, closePopover]
  );
  return (
    <div className="relative">
      <RefButtonHelper
        className="z-0 relative w-full bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-sm rounded-md pl-3 pr-10 py-2 text-left cursor-default sm:text-sm"
        onClick={togglePopover}
        ref={referencePopoverCallbackRef}
      >
        <span className="block truncate">
          <Translate id={selectedOption?.i18nText} />
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <Icon
            icon={IconsEnum.CHEVRON_DOWN}
            className="h-5 w-5 text-gray-400 dark:text-gray-500"
          />
        </span>
      </RefButtonHelper>

      {isPopoverOpen ? (
        <Popover
          className="z-10 absolute w-full min-w-60 max-h-[300px] text-left bg-white dark:bg-gray-850 border border-gray-300 dark:border-gray-700 shadow-lg dark: dark:glow-md rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
          closePopover={closePopover}
          distanceOffset={dropdownMenuOffset}
          placement={PopoverPlacement.BOTTOM_START}
          referenceElement={referencePopoverElement}
          closeOnOuterClick
          positionStyle={PositionStyleEnum.absolute}
          withoutCalculateMaxHeight
        >
          <ul>
            {options.map(({ value, i18nText }) => (
              <UniversalFilterSelectItem<T>
                key={toString(value)}
                value={value}
                i18nText={i18nText}
                isSelected={value === selectedValue}
                onChange={handleChange}
              />
            ))}
          </ul>
        </Popover>
      ) : null}
    </div>
  );
}

export default UniversalFilterOptionSelect;
